/**
 * Bridge API client. Same-origin `fetch` with the session cookie, JSON bodies, typed responses,
 * `ApiError {status, code, body}`, timeouts (30 s default, 20 s search/actions, 60 s bulk),
 * cancellation through `RequestGroup.latest()` and a global `onUnauthorized` hook.
 * Bodies are never logged.
 */
import type * as T from './types';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly body: unknown = null,
  ) {
    super(code);
    this.name = 'ApiError';
  }
}

/** `true` for the error a superseded `RequestGroup.latest()` call rejects with. */
export function isAborted(err: unknown): boolean {
  return err instanceof ApiError && err.code === 'aborted';
}

export const DEFAULT_TIMEOUT_MS = 30_000;
export const SEARCH_TIMEOUT_MS = 20_000;
export const ACTIONS_TIMEOUT_MS = 20_000;
export const BULK_TIMEOUT_MS = 60_000;

export interface RequestOptions {
  method?: 'GET' | 'POST';
  /** Serialised as JSON unless it is FormData or a string. */
  body?: unknown;
  timeoutMs?: number;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

/** Called on every 401 (except login). The store registers a handler that shows the login screen. */
export let onUnauthorized: ((err: ApiError) => void) | null = null;
export function setUnauthorizedHandler(fn: ((err: ApiError) => void) | null): void {
  onUnauthorized = fn;
}

/** Combine an optional caller signal with a timeout into one signal. */
function combinedSignal(timeoutMs: number, external?: AbortSignal): { signal: AbortSignal; clear: () => void } {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(new DOMException('timeout', 'TimeoutError')), timeoutMs);
  const onAbort = () => ctrl.abort(external?.reason);
  if (external) {
    if (external.aborted) onAbort();
    else external.addEventListener('abort', onAbort, { once: true });
  }
  return {
    signal: ctrl.signal,
    clear: () => {
      clearTimeout(timer);
      external?.removeEventListener('abort', onAbort);
    },
  };
}

/**
 * Core transport (legacy `ot`). Network failure → `ApiError(0,"network")`; abort → `"aborted"`;
 * timeout → `"timeout"`; non-2xx → `ApiError(status, body.error ?? "error", body)`.
 */
export async function request<R>(path: string, opts: RequestOptions = {}): Promise<R> {
  const isForm = typeof FormData !== 'undefined' && opts.body instanceof FormData;
  const headers: Record<string, string> = { ...(opts.headers ?? {}) };
  if (!isForm) headers['Content-Type'] = 'application/json';
  const body = isForm ? (opts.body as FormData) : typeof opts.body === 'string' ? opts.body : opts.body !== undefined ? JSON.stringify(opts.body) : undefined;
  const { signal, clear } = combinedSignal(opts.timeoutMs ?? DEFAULT_TIMEOUT_MS, opts.signal);
  let res: Response;
  try {
    res = await fetch(path, { method: opts.method ?? 'GET', headers, body, credentials: 'same-origin', signal });
  } catch (err) {
    clear();
    const name = (err as { name?: string })?.name;
    if (name === 'TimeoutError' || signal.reason?.name === 'TimeoutError') throw new ApiError(0, 'timeout');
    if (name === 'AbortError' || signal.aborted) throw new ApiError(0, 'aborted');
    throw new ApiError(0, 'network');
  }
  clear();
  if (!res.ok) {
    let parsed: unknown = null;
    let code = 'error';
    try {
      parsed = await res.json();
      const e = (parsed as { error?: unknown })?.error;
      if (typeof e === 'string' && e) code = e;
    } catch {
      /* non-JSON error body */
    }
    const err = new ApiError(res.status, code, parsed);
    if (res.status === 401 && !path.startsWith('/api/auth/login')) onUnauthorized?.(err);
    throw err;
  }
  try {
    return (await res.json()) as R;
  } catch {
    return {} as R;
  }
}

const get = <R>(path: string, o: RequestOptions = {}) => request<R>(path, { ...o, method: 'GET' });
const post = <R>(path: string, body: unknown, o: RequestOptions = {}) => request<R>(path, { ...o, method: 'POST', body });

/**
 * Keyed cancellation: `latest(key, fn)` aborts the previous in-flight request of that key and
 * rejects it with `ApiError(0,"aborted")`, so opening messages A→B→C only resolves C.
 */
export class RequestGroup {
  private readonly ctrls = new Map<string, AbortController>();

  latest<R>(key: string, fn: (signal: AbortSignal) => Promise<R>): Promise<R> {
    this.cancel(key);
    const ctrl = new AbortController();
    this.ctrls.set(key, ctrl);
    return fn(ctrl.signal).then(
      (v) => {
        if (this.ctrls.get(key) === ctrl) this.ctrls.delete(key);
        if (ctrl.signal.aborted) throw new ApiError(0, 'aborted');
        return v;
      },
      (err) => {
        if (this.ctrls.get(key) === ctrl) this.ctrls.delete(key);
        if (ctrl.signal.aborted) throw new ApiError(0, 'aborted');
        throw err;
      },
    );
  }

  cancel(key: string): void {
    const prev = this.ctrls.get(key);
    if (prev) {
      prev.abort(new DOMException('superseded', 'AbortError'));
      this.ctrls.delete(key);
    }
  }

  cancelAll(): void {
    for (const k of [...this.ctrls.keys()]) this.cancel(k);
  }

  inFlight(key: string): boolean {
    return this.ctrls.has(key);
  }
}

/** Shared groups: `"message"` for the reader, `"search"` for the search box. */
export const groups = new RequestGroup();

const enc = encodeURIComponent;

export interface MessagesParams {
  limit?: number;
  beforeSeq?: number;
  reconcile?: boolean;
}

export const api = {
  // ---- auth / health
  /** Password step. `needs2fa` means no cookie was issued: continue with `twofaLogin`. */
  login: (user: string, pass: string) =>
    post<{ ok: boolean; user: string; needs2fa?: boolean; pending?: string; mustEnroll?: boolean }>('/api/auth/login', { user, pass }),
  logout: () => post<{ ok: boolean }>('/api/auth/logout', undefined),
  me: () => get<{ user: string; twofa?: boolean; admin?: boolean; mustEnroll?: boolean }>('/api/auth/me'),
  health: () => get<{ ok: boolean; redis: boolean; ts: string }>('/api/health'),
  ready: () => get<T.ReadyResponse>('/api/ready'),
  tenant: () => get<T.TenantInfo>('/api/tenant', { timeoutMs: 8000 }),

  // ---- two-factor authentication
  twofaStatus: () => get<T.TwoFactorStatus>('/api/2fa'),
  twofaSetup: () => post<T.TwoFactorSetup>('/api/2fa/setup', {}),
  twofaConfirm: (code: string) => post<{ ok: boolean; recovery: string[] }>('/api/2fa/confirm', { code }),
  twofaDisable: (code: string) => post<{ ok: boolean; enabled: boolean }>('/api/2fa/disable', { code }),
  /** Second login step: exchange the pending id from `login()` for a session. */
  twofaLogin: (pending: string, code: string) =>
    post<{ ok: boolean; user: string; usedRecovery?: boolean; recoveryLeft?: number; mustEnroll?: boolean }>('/api/auth/2fa', { pending, code }),

  // ---- domain administration
  adminWhoami: () => get<T.AdminWhoami>('/api/admin/whoami'),
  adminMailboxes: (domain?: string) =>
    get<{ domain: string; mailboxes: T.AdminMailbox[] }>('/api/admin/mailboxes' + (domain ? `?domain=${enc(domain)}` : ''), { timeoutMs: 60_000 }),
  adminCreateMailbox: (b: { user: string; password: string; realName?: string; quotaMb?: number | null; domain?: string }) =>
    post<{ ok: boolean; mailbox: T.AdminMailbox | null; senderMap: string }>('/api/admin/mailboxes', b, { timeoutMs: 180_000 }),
  adminSetPassword: (b: { user: string; password: string; domain?: string }) =>
    post<{ ok: boolean; email: string }>('/api/admin/mailboxes/password', b, { timeoutMs: 120_000 }),
  adminSetQuota: (b: { user: string; quotaMb: number | null; domain?: string }) =>
    post<{ ok: boolean; email: string; quotaBytes: number | null }>('/api/admin/mailboxes/quota', b, { timeoutMs: 120_000 }),

  // ---- folders
  folders: () => get<{ folders: T.ApiFolder[] }>('/api/folders'),
  createFolder: (name: string, parent?: string) => post<{ ok: boolean; path: string }>('/api/folders', parent ? { name, parent } : { name }),
  renameFolder: (path: string, name: string, parent?: string) =>
    post<{ ok: boolean; path: string }>('/api/folders/rename', parent === undefined ? { path, name } : { path, name, parent }),
  deleteFolder: (path: string) => post<{ ok: boolean }>('/api/folders/delete', { path }),
  markFolderRead: (folder: string) => post<{ ok: boolean; marked: number }>('/api/folder/mark-read', { folder }),
  emptyFolder: (folder: string) => post<{ ok: boolean; purged: number }>('/api/folder/empty', { folder }),
  quota: () => get<T.QuotaResponse>('/api/quota'),
  labels: (folder: string) => get<T.LabelsResponse>(`/api/labels?folder=${enc(folder)}`),

  // ---- messages
  messages: (folder: string, p: MessagesParams = {}, o: RequestOptions = {}) =>
    get<T.MessagesResponse>(
      `/api/messages?folder=${enc(folder)}&limit=${p.limit ?? 50}` + (p.beforeSeq ? `&beforeSeq=${p.beforeSeq}` : '') + (p.reconcile ? '&reconcile=1' : ''),
      o,
    ),
  sync: (folder: string, cursor: T.SyncCursor, o: RequestOptions = {}) =>
    get<T.SyncResponse>(
      `/api/sync?folder=${enc(folder)}&uidValidity=${enc(cursor.uidValidity)}&modseq=${enc(cursor.highestModseq)}&uidNext=${enc(cursor.uidNext)}`,
      o,
    ),
  previews: (folder: string, uids: readonly number[]) => get<T.PreviewsResponse>(`/api/previews?folder=${enc(folder)}&uids=${uids.join(',')}`),
  message: (folder: string, uid: number, o: RequestOptions = {}) => get<T.MessageDetail>(`/api/message/${uid}?folder=${enc(folder)}`, o),
  sourceUrl: (folder: string, uid: number, download = false) => `/api/message/${uid}/source?folder=${enc(folder)}` + (download ? '&download=1' : ''),
  attachmentUrl: (folder: string, uid: number, part: string, inline = false) => `/api/attachment/${uid}/${part}?folder=${enc(folder)}` + (inline ? '&inline=1' : ''),
  attachmentsZipUrl: (folder: string, uid: number) => `/api/attachments/${uid}/zip?folder=${enc(folder)}`,
  attachedMessage: (folder: string, uid: number, part: string) =>
    get<{ subject: string; from: { name: string; address: string }[]; to: { name: string; address: string }[]; cc: { name: string; address: string }[]; date: string | null; html: string | null; text: string; attachments: { filename: string }[] }>(
      `/api/attachment/${uid}/${part}/message?folder=${enc(folder)}`,
    ),

  // ---- actions
  actions: (folder: string, uids: readonly number[], op: string, dest?: string, label?: string) =>
    post<T.ActionsResponse>('/api/actions', { folder, uids, op, dest, label }, { timeoutMs: ACTIONS_TIMEOUT_MS }),
  snooze: (folder: string, uids: readonly number[], until: number) => post<{ ok: boolean; wakeAt: number; folder: string }>('/api/snooze', { folder, uids, until }),
  unsnooze: (uids: readonly number[]) => post<{ moved: number }>('/api/snooze/cancel', { uids }),
  wakeSnoozed: () => post<{ woke: number; pending: number }>('/api/snooze/wake', {}),
  mute: (folder: string, uid: number) => post<T.MuteResponse>('/api/mute', { folder, uid }),
  muted: () => get<{ muted: { id: string; subject: string }[] }>('/api/muted'),
  unmute: (id: string) => post<{ ok: boolean; muted: number }>('/api/muted/delete', { id }),
  unsubscribe: (folder: string, uid: number) => post<T.UnsubscribeResponse>('/api/unsubscribe', { folder, uid }, { timeoutMs: ACTIONS_TIMEOUT_MS }),

  // ---- sending
  send: (form: FormData) => post<T.SendResponse>('/api/send', form),
  cancelSend: (id: string) => post<T.CancelSendResponse>('/api/send/cancel', { id }),
  outbox: () => get<T.OutboxResponse>('/api/outbox'),
  saveDraft: (d: { to: string; cc: string; bcc: string; subject: string; text: string; html: string; draftUid?: number }) => post<T.DraftResponse>('/api/draft', d),
  deleteDraft: (uid: number) => post<{ ok: boolean }>('/api/draft/delete', { uid }),

  // ---- search
  search: (folder: string, q: SearchParams, o: RequestOptions = {}) => {
    const p = new URLSearchParams({ folder, q: q.text ?? '' });
    if (q.from) p.set('from', q.from);
    if (q.to) p.set('to', q.to);
    if (q.subject) p.set('subject', q.subject);
    if (q.unseen) p.set('unseen', '1');
    if (q.seen) p.set('seen', '1');
    if (q.flagged) p.set('flagged', '1');
    if (q.hasAttachment) p.set('hasAttachment', '1');
    if (q.list) p.set('list', '1');
    if (q.personal) p.set('personal', '1');
    if (q.scope) p.set('scope', q.scope);
    if (q.after) p.set('after', q.after);
    if (q.before) p.set('before', q.before);
    if (q.label) p.set('label', q.label);
    return get<T.SearchResponse>(`/api/search?${p}`, { timeoutMs: SEARCH_TIMEOUT_MS, ...o });
  },
  searchCount: (folder: string, flags: Record<string, string>) =>
    get<T.SearchResponse>('/api/search?' + new URLSearchParams({ folder, q: '', count: '1', ...flags }).toString(), { timeoutMs: SEARCH_TIMEOUT_MS }),
  searchAct: (body: Record<string, unknown>) => post<T.SearchActResponse>('/api/search/act', body, { timeoutMs: BULK_TIMEOUT_MS }),
  searches: () => get<{ searches: { id: string; name: string; q: string }[] }>('/api/searches'),
  addSearch: (name: string, q: string) => post<{ searches: { id: string; name: string; q: string }[] }>('/api/searches', { name, q }),
  deleteSearch: (id: string) => post<{ searches: { id: string; name: string; q: string }[] }>('/api/searches/delete', { id }),

  // ---- prefs
  templates: () => get<{ templates: { id: string; name: string; body: string }[] }>('/api/templates'),
  addTemplate: (name: string, body: string) => post<{ templates: { id: string; name: string; body: string }[] }>('/api/templates', { name, body }),
  deleteTemplate: (id: string) => post<{ templates: { id: string; name: string; body: string }[] }>('/api/templates/delete', { id }),
  contacts: () => get<{ contacts: { id: string; name: string; email: string; source?: string }[] }>('/api/contacts'),
  addContact: (name: string, email: string) => post<{ contacts: { id: string; name: string; email: string }[] }>('/api/contacts', { name, email }),
  deleteContact: (id: string) => post<{ contacts: { id: string; name: string; email: string }[] }>('/api/contacts/delete', { id }),
  importContacts: () => post<{ imported: number; total?: number }>('/api/contacts/import', {}),
  senders: () => get<{ senders: { id: string; name: string }[] }>('/api/senders'),
  trustSender: (address: string) => post<{ senders: { id: string; name: string }[] }>('/api/senders', { address }),
  untrustSender: (id: string) => post<{ senders: { id: string; name: string }[] }>('/api/senders/delete', { id }),

  // ---- misc
  live: () => get<T.LiveResponse>('/api/live'),
  eventsUrl: () => '/api/events?v=2',

  // ---- push (plain JSON, same wrapper)
  pushStatus: () => get<T.PushStatusResponse>('/api/push/status'),
  pushKey: () => get<{ key: string }>('/api/push/key'),
  pushSubscribe: (subscription: unknown, lang: string, ua: string) => post<{ ok: boolean; devices: number }>('/api/push/subscribe', { subscription, lang, ua }),
  pushUnsubscribe: (endpoint: string) => post<{ ok: boolean; devices: number }>('/api/push/unsubscribe', { endpoint }),
  pushTest: () => post<{ sent: number; gone?: number; failed?: number; left?: number }>('/api/push/test', {}),
};

export interface SearchParams {
  text?: string;
  from?: string;
  to?: string;
  subject?: string;
  unseen?: boolean;
  seen?: boolean;
  flagged?: boolean;
  hasAttachment?: boolean;
  list?: boolean;
  personal?: boolean;
  scope?: string;
  after?: string;
  before?: string;
  label?: string;
}

export type Api = typeof api;
